import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import { EventObserver } from '../../global/event-observer';
import { Labels } from '../../global/labels';
import {
    createElement,
    element,
    rebuild,
    revertClassList,
    updateClassList,
} from '../../global/ui-helpers';
import type { CallbackFn, NullOr } from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import '../buttonbar/ui-buttonbar';
import type { UIButtonbar } from '../buttonbar/ui-buttonbar';
import { UIField } from '../field/ui-field';
import { UIElement } from '../ui-element';
import type { ElementConfig } from '../ui-element.type';
import type { UIView } from '../views/ui-view';
import type { UIViews } from '../views/ui-views';
import styles from './ui-wizard.css';
import type {
    AnimationConfig,
    FormWithValidator,
    WizardNextEventDetail,
} from './ui-wizard.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIWizard
 * @element ui-wizard
 * @classdesc Represents a class for <code>ui-wizard</code> element.
 * Wizard is used to create multi-step forms.
 * @fires event:wizard-next
 * @fires event:wizard-back
 * @fires event:wizard-cancel
 * @property {number} [selectedIndex] {@attr selected-index} Which step is activated.
 * @property {number} [lastActivatedStep] {@attr last-activated-step} Highest activated step.
 * @property {boolean} [animated] {@attr animated} Is animation between steps enabled/disabled.
 * @property {boolean} [external] {@attr external} If UIWizard has external control.
 * @property {string} [labelCancel] {@attr label-cancel} If is set this label will be
 * put to the cancel button.
 * @property {string} [labelBack] {@attr label-back} If is set this label will be
 * put to the back button.
 * @property {string} [labelNext] {@attr label-next} If is set this label will be put
 * to the next button.
 * @property {boolean} [standalone] {@attr standalone} Appends <code>form-content</code> class to ui-views element.
 * @property {boolean} [subNavigation] {@attr sub-navigation} Allows to navigate between passed
 * steps using step captions
 * @property {UIViews} views Shortcut to views.
 * @property {NodeListOf<UIView>} viewList Shortcut to view nodes.
 * @property {UIButtonbar} buttonBar Shortcut to button bar.
 * @property {HTMLButtonElement} nextButton Shortcut to next button.
 * @property {HTMLButtonElement} backButton Shortcut to back button.
 * @property {HTMLButtonElement} cancelButton Shortcut to cancel button.
 * @slot {@type "ui-view"}
 * @example
 * <ui-wizard>
 *   <ui-view>...</ui-view>
 *   <ui-view>...</ui-view>
 *   <ui-view>...</ui-view>
 * </ui-wizard>
 */
@Component({ tag: 'ui-wizard', styles })
export class UIWizard extends UIElement {
    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-wizard', {
            next: 'Continue',
            back: 'Back',
            cancel: 'Cancel',
            completedStep: 'Completed step',
        });
    }

    @Attribute(String) labelCancel?: string;
    @Attribute(String) labelBack?: string;
    @Attribute(String) labelNext?: string;
    @Attribute(Number) selectedIndex: number = 1;
    @Attribute(Number) lastActivatedStep: number = 1;
    @Attribute(Boolean) animated?: boolean;
    @Attribute(Boolean) external?: boolean;
    @Attribute(Boolean) standalone?: boolean;
    @Attribute(Boolean) subNavigation?: boolean;

    @Child('ui-buttonbar.ui-wizard__nav') buttonBar!: UIButtonbar;
    @Child('.ui-wizard__nav .-next') nextButton!: HTMLButtonElement;
    @Child('.ui-wizard__nav .-back') backButton!: HTMLButtonElement;
    @Child('.ui-wizard__nav .-cancel') cancelButton!: HTMLButtonElement;
    @Child(':scope > ui-views') views!: UIViews;
    @Children(':scope > ui-views > ui-view, :scope > ui-view')
    viewList!: NodeListOf<UIView>;

    private readonly clickObserver = new EventObserver({ name: 'click' });
    private readonly mutationObserver = new MutationObserver(
        this.handleMutations.bind(this)
    );

    /**
     * Returns current step index
     * @returns {number}
     */
    getCurrentStep(): number {
        return this.selectedIndex;
    }

    /**
     * Changes current step index
     * @param {number} [step]
     */
    setCurrentStep(step: number = 1): void {
        this.selectedIndex =
            step > 0 && step <= this.views.children.length ? step : 1;
        this.focusWizardTop();
    }

    /**
     * Checks if the current step is the first one.
     * @returns {boolean}
     */
    isInitialStep(): boolean {
        return this.selectedIndex === 1;
    }

    /**
     * Checks if the current step is the last one.
     * @returns {boolean}
     */
    isLastStep(): boolean {
        return this.selectedIndex >= this.views.children.length;
    }

    /**
     * Does transition between two views.
     * @param {AnimationConfig} currentView
     * @param {AnimationConfig} nextView
     * @param {Function | null} [callback]
     */
    performTransition(
        currentView: AnimationConfig,
        nextView: AnimationConfig,
        callback: NullOr<CallbackFn> = null
    ): void {
        if (!this.animated) {
            if (typeof callback === 'function') {
                callback();
            }
            return;
        }
        updateClassList(currentView.element, currentView.prepare);
        // reflow trick, do not remove for smoother animation
        currentView.element.offsetWidth.toString();

        updateClassList(nextView.element, nextView.prepare);
        // reflow trick, do not remove for smoother animation
        nextView.element.offsetWidth.toString();

        updateClassList(currentView.element, currentView.run);

        const nextViewComplete = (): void => {
            nextView.element.removeEventListener(
                'transitionend',
                nextViewComplete
            );
            revertClassList(nextView.element, nextView.run);
            revertClassList(nextView.element, nextView.prepare);

            if (typeof callback === 'function') {
                callback();
            }
        };

        const currentViewComplete = (): void => {
            currentView.element.removeEventListener(
                'transitionend',
                currentViewComplete
            );
            revertClassList(currentView.element, currentView.run);
            revertClassList(currentView.element, currentView.prepare);

            updateClassList(nextView.element, nextView.run);
            nextView.element.addEventListener(
                'transitionend',
                nextViewComplete
            );
            // reflow trick, do not remove for smoother animation
            nextView.element.offsetWidth.toString();
        };

        currentView.element.addEventListener(
            'transitionend',
            currentViewComplete
        );
    }

    /**
     * @param {UIView} currentView
     * @param {UIView} nextView
     */
    switchViews(currentView: UIView, nextView: UIView): void {
        this.performTransition(
            {
                element: currentView,
                prepare: {
                    '-display-effect': true,
                    '-fade-out': true,
                },
                run: {
                    '-animating': true,
                },
            },
            {
                element: nextView,
                prepare: {
                    '-display-effect': true,
                    '-fade-in': true,
                    '-no-space': true,
                },
                run: {
                    '-animating': true,
                    '-no-space': false,
                },
            }
        );
    }

    /**
     * Navigate to the previous view.
     */
    back(): void {
        if (this.isInitialStep()) {
            return;
        }
        const currentView = this.views.getActiveView()!;
        const nextView = currentView.previousElementSibling! as UIView;
        this.switchViews(currentView, nextView);
        this.selectedIndex--;
        this.focusWizardTop();
    }

    /**
     * Navigate to the specific view by given id.
     * @param {number} stepId
     */
    activateStep(stepId: number): void {
        const currentView = this.views.getActiveView()!;
        const nextView = this.viewList[stepId - 1];
        if (currentView === nextView) {
            return;
        }
        this.switchViews(currentView, nextView);
        this.selectedIndex = stepId;
        this.focusWizardTop();
    }

    /**
     * Navigate to the next view.
     */
    next(): void {
        if (this.isLastStep() || !this.isStepValid()) {
            return;
        }
        const currentView = this.views.getActiveView()!;
        const nextView = currentView.nextElementSibling! as UIView;
        this.switchViews(currentView, nextView);
        this.selectedIndex++;
        this.focusWizardTop();
    }

    /**
     * Navigate to the first view.
     */
    cancel(): void {
        const currentView = this.views.getActiveView()!;
        const nextView = this.viewList[0];
        if (currentView === nextView) {
            return;
        }
        this.switchViews(currentView, nextView);
        this.selectedIndex = 1;
        this.focusWizardTop();
    }

    /**
     * Clean-up errors on step display.
     * @param {UIView} view
     * @returns {UIView}
     * @private
     */
    private cleanErrors(view: UIView): UIView {
        view.querySelectorAll<UIField>('ui-field').forEach((field) => {
            const controlId = field.getControlId();
            const control = field.querySelector<HTMLInputElement>(
                `[id="${controlId}"]`
            );
            if (control && !control.validity.valid) {
                field.removeErrors();
            }
        });
        return view;
    }

    /**
     * Fires callback when the step has been changed.
     * @private
     */
    private onChangeStep(): void {
        this.updateWizardNavigation();
        this.querySelectorAll('.ui-wizard__steps li').forEach((node, index) => {
            if (!this.viewList[index]) {
                return;
            }
            rebuild(node, this.buildStepCaption(this.viewList[index], index));
        });
    }

    updateWizardNavigation(): void {
        const toggleHidden = this.selectedIndex > 1 ? this.show : this.hide;
        if (this.backButton && !this.buttonBar.classList.contains('-custom')) {
            toggleHidden.call(this.backButton);
        }
        if (this.selectedIndex > this.lastActivatedStep) {
            this.lastActivatedStep = this.selectedIndex;
        }
        this.views.setAttribute('selected-index', String(this.selectedIndex));
    }

    /**
     * Handles click on the "Next" button.
     * @private
     */
    private handleActionNext = (): void => {
        if (this.external) {
            this.dispatchCustomEvent('wizard-next', {});
        } else {
            this.next();
        }
    };

    /**
     * Handles click on the "Cancel" button.
     * @private
     */
    private handleActionCancel = (): void => {
        if (this.external) {
            this.dispatchCustomEvent('wizard-cancel', {});
        } else {
            this.cancel();
        }
    };

    /**
     * Handles click on the "Next" button.
     * @param {DeclarativeEvent} e
     * @private
     */
    private handleActionChange = (e: Event): void => {
        const detail = (e as CustomEvent<WizardNextEventDetail>).detail;
        const stepId = Number(detail.relatedTarget?.dataset?.stepId) || 1;
        if (this.external) {
            this.dispatchCustomEvent('wizard-next', { stepId });
        } else {
            this.activateStep(stepId);
        }
    };

    /**
     * Handles click on the "Back" button.
     * @private
     */
    private handleActionBack = (): void => {
        if (this.external) {
            this.dispatchCustomEvent('wizard-back', {});
        } else {
            this.back();
        }
    };

    /**
     * Handles component mutations
     * @param {MutationRecord} mutation
     * @private
     */
    private handleAttributesMutation(mutation: MutationRecord): void {
        if (mutation.attributeName === 'selected-index') {
            this.onChangeStep();
        }
    }

    /**
     * Fires callback when component mutates.
     * @param {Array<MutationRecord>} mutations
     * @private
     */
    private handleMutations(mutations: Array<MutationRecord>): void {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
                this.handleAttributesMutation(mutation);
            }
        });
    }

    /**
     * Checks if current step is valid before invoking next(). If step is valid
     * go to next step, otherwise show errors for validation.
     * @returns {boolean}
     */
    isStepValid(): boolean {
        const currentStep = this.views.getActiveView();
        const form = this.closest<FormWithValidator>('form');
        let valid = true;
        if (form?.validator?.shouldFormValidate()) {
            form.validator.resolveElementsRequirability();
            form.validator.validate();
            form.checkValidity();
            const elems = currentStep?.querySelectorAll<HTMLInputElement>(
                'input,select,textarea'
            );
            if (elems && elems.length > 0) {
                valid = Array.from(elems).every((elem) => {
                    return elem.willValidate ? elem.checkValidity() : true;
                });
            }
        }
        return valid;
    }

    /**
     * Renders button bar.
     * @returns {UIButtonbar}
     */
    private renderButtonBar(): UIButtonbar {
        const buttonbar = this.queryChildren('ui-buttonbar')[0];
        return createElement<UIButtonbar>({
            tagName: 'ui-buttonbar',
            element: buttonbar,
            classList: {
                'ui-wizard__nav': true,
                '-custom': !!buttonbar,
            },
            attributes: buttonbar
                ? undefined
                : {
                      alignment: 'split',
                  },
            children: buttonbar
                ? []
                : [
                      {
                          tagName: 'ui-button',
                          attributes: {
                              'layout': 'tertiary',
                              'class': '-cancel',
                              'data-event': 'wizard-action-cancel',
                          },
                          classList: {},
                          children: [
                              this.labelCancel || UIWizard.labels.cancel,
                          ],
                      },
                      {
                          tagName: 'button',
                          attributes: {
                              'type': 'button',
                              'class': 'button -next',
                              'data-event': 'wizard-action-next',
                          },
                          children: [this.labelNext || UIWizard.labels.next],
                      },
                      {
                          tagName: 'button',
                          attributes: {
                              'type': 'button',
                              'class': 'button -back -guiding -destructive',
                              'data-event': 'wizard-action-back',
                          },
                          children: [this.labelBack || UIWizard.labels.back],
                      },
                  ],
        })!;
    }

    /**
     * Renders views.
     * @returns {UIViews}
     */
    private renderViews(): UIViews {
        // wrap contents of ui-view inside .ui-wizard__content
        this.viewList.forEach((view) => {
            const content = view.querySelector<HTMLElement>(
                '.ui-wizard__content'
            );
            if (!content) {
                const children = Array.from(view.childNodes).filter(
                    (child) =>
                        !(child instanceof HTMLElement) ||
                        (child.localName !== 'ui-legalblock' &&
                            child.localName !== 'ui-buttonbar')
                );
                if (children.length) {
                    const wrapper = element`<div class="ui-wizard__content"></div>`;
                    for (const child of children) {
                        wrapper.appendChild(child);
                    }
                    view.insertBefore(wrapper, view.firstChild);
                }
            }
        });

        return (
            this.views ||
            createElement<UIViews>({
                tagName: 'ui-views',
                classList: {
                    'form-content': !this.standalone,
                },
                attributes: {
                    focusonchange: '',
                },
                children: this.viewList,
            })
        );
    }

    /**
     * Build config for step caption
     * @param {UIView} view
     * @param {number} index
     * @returns {ElementConfig}
     */
    buildStepCaption(view: UIView, index: number): ElementConfig {
        const labelText = view.label ? view.label : '';
        const label =
            this.subNavigation && index < this.lastActivatedStep
                ? ({
                      tagName: 'button',
                      attributes: {
                          'class': 'ui-wizard__link',
                          'data-event': 'wizard-action-change',
                          'data-step-id': index + 1,
                          'type': 'button',
                      },
                      children: [labelText],
                  } satisfies ElementConfig)
                : labelText;
        return {
            tagName: 'li',
            classList: {
                'ui-wizard__step': true,
                '-passed': index < this.selectedIndex - 1,
                '-active': index === this.selectedIndex - 1,
            },
            attributes: {
                'aria-label':
                    index < this.selectedIndex - 1
                        ? UIWizard.labels.completedStep
                        : null,
                'aria-current':
                    index === this.selectedIndex - 1 ? 'step' : null,
            },
            children: [label, index > 0 ? { tagName: 'ins' } : ''],
        };
    }

    /**
     * Renders steps.
     * @param {UIViews} views
     * @returns {HTMLElement}
     */
    private renderSteps(views: UIViews): HTMLElement {
        return this.createElement({
            tagName: 'ol',
            classList: {
                'ui-wizard__steps': true,
            },
            children: Array.from(views.children).map((view, index) =>
                this.buildStepCaption(view as UIView, index)
            ),
        });
    }

    /**
     * Scrolls the steps area into view so the step counter is visible after navigation.
     * Focus on the first interactive element is handled by ui-views focusonchange.
     */
    focusWizardTop(): void {
        const steps = this.querySelector<HTMLElement>('.ui-wizard__steps');
        steps?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.clickObserver.observe(this);
        this.mutationObserver.observe(this, { attributes: true });
        this.addEventListener('wizard-action-back', this.handleActionBack);
        this.addEventListener('wizard-action-next', this.handleActionNext);
        this.addEventListener('wizard-action-cancel', this.handleActionCancel);
        this.addEventListener('wizard-action-change', this.handleActionChange);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.clickObserver.disconnect();
        this.mutationObserver.disconnect();
        this.removeEventListener('wizard-action-back', this.handleActionBack);
        this.removeEventListener('wizard-action-next', this.handleActionNext);
        this.removeEventListener(
            'wizard-action-cancel',
            this.handleActionCancel
        );
        this.removeEventListener(
            'wizard-action-change',
            this.handleActionChange
        );
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const views = this.renderViews();
        views.setAttribute('focusonchange', '');
        const buttonBar = this.renderButtonBar();
        const steps = this.renderSteps(views);
        this.detachChildNodes();
        this.insertElements([steps, views, buttonBar]);
        if (this.backButton && !this.backButton.hasAttribute('data-event')) {
            this.backButton.setAttribute('data-event', 'wizard-action-back');
        }
        if (this.nextButton && !this.nextButton.hasAttribute('data-event')) {
            this.nextButton.setAttribute('data-event', 'wizard-action-next');
        }
        if (
            this.cancelButton &&
            !this.cancelButton.hasAttribute('data-event')
        ) {
            this.cancelButton.setAttribute(
                'data-event',
                'wizard-action-cancel'
            );
        }
        this.updateWizardNavigation();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
